import { CheckBoldIcon } from '@zep-us/zus-icons';
import { useTranslation } from 'next-i18next';
import { JellyIcon, SlashCircleIcon } from '@/icons';
import { Chip } from '@/ui_v3';
import { cn } from '@/utils';
import type { PartGridItem } from '../avatarDecorate.logic';

/**
 * 카탈로그 로딩 중 표시. 실제 그리드와 동일한 컬럼/간격/셀 크기를 써서
 * 로딩 → 렌더 전환 시 레이아웃이 튀지 않게 한다.
 */
export const AvatarItemGridSkeleton = ({
  hidePointUi = false,
  count = 10
}: {
  hidePointUi?: boolean;
  count?: number;
}) => <ul aria-hidden="true" className={cn(
// 실제 그리드와 동일한 auto-fill 규칙(열 수가 폭에 따라 바뀜)을 그대로 쓴다.
'grid grid-cols-[repeat(auto-fill,minmax(80px,1fr))] gap-x-[8px]', hidePointUi ? 'gap-y-sm' : 'gap-y-md')} data-sentry-component="AvatarItemGridSkeleton" data-sentry-source-file="AvatarItemGrid.tsx">
    {Array.from({
    length: count
  }, (_, i) => <li key={i}
  // 셀마다 살짝 늦게 시작시켜 한 덩어리로 깜빡이지 않고 좌상→우하로 훑고
  // 지나가는 결이 생기게 한다.
  style={{
    animationDelay: `${i * 70}ms`
  }} className="flex animate-fade flex-col items-center animate-duration-300 [&>*]:[animation-delay:inherit]">
        <div className={cn('skeleton-shimmer h-[106px] w-full rounded-[6px]', !hidePointUi && 'mb-[-14px]')} />
        {!hidePointUi && <div className="relative z-[1] flex min-h-[26px] items-center">
            <div className="h-[26px] w-[52px] rounded-full skeleton-shimmer" />
          </div>}
      </li>)}
  </ul>;
export const AvatarItemGrid = ({
  items,
  selectedShapeId,
  onSelect,
  /**
   * 비학생(선생님·일반·기업) = 포인트 UI 미노출. 전 아이템 열림 취급,
   * 무료/가격 칩 숨김 (PRD §E / §10-2 C7).
   */
  hidePointUi = false,
  /** 액세서리 탭에서만 '없음' 타일을 맨 앞에 노출한다. */
  showNoneOption = false,
  isNoneSelected = false,
  onSelectNone,
  /** 방금 구매 성공한 shape. 그 카드에만 획득 연출(팝·링·배지)을 얹는다. */
  justPurchasedShapeId = null
}: {
  items: PartGridItem[];
  selectedShapeId: number | null;
  onSelect: (id: number) => void;
  hidePointUi?: boolean;
  showNoneOption?: boolean;
  isNoneSelected?: boolean;
  onSelectNone?: () => void;
  justPurchasedShapeId?: number | null;
}) => {
  const {
    t
  } = useTranslation(['common']);
  const chipClassName = 'h-[26px] min-h-[26px] rounded-full border-alternative bg-default px-[8px] text-caption-lg-semibold text-neutral shadow-none';
  return <ul className={cn(
  // Figma(1333:56218): flex-wrap + 카드 flex-[1_0_0] min-w-[80px].
  // 즉 폭이 줄면 카드가 쪼그라드는 게 아니라 '한 줄에 들어가는 개수'가 바뀐다.
  // grid-cols-5 로 고정하면 카드만 작아지므로 auto-fill 에 맡긴다.
  // (520px 컨테이너에서는 자연히 5열이 된다)
  'grid grid-cols-[repeat(auto-fill,minmax(80px,1fr))] gap-x-[8px]',
  // 학생은 아이템 아래 포인트 칩이 mb-[-14px] 로 겹쳐 올라와 세로 여백이 더 필요하다.
  // 비학생(포인트 UI 미노출)은 칩이 없으므로 12px 로 좁힌다.
  hidePointUi ? 'gap-y-sm' : 'gap-y-md')} data-sentry-component="AvatarItemGrid" data-sentry-source-file="AvatarItemGrid.tsx">
      {showNoneOption &&
    // 액세서리는 '같은 항목 재클릭 = 해제' 토글이라 해제 방법이 눈에 보이지 않았다.
    // 맨 앞에 명시적인 '없음' 타일을 둬서 학생이 바로 벗을 수 있게 한다.
    <li onClick={onSelectNone} className="group flex cursor-pointer flex-col items-center">
          <div className={cn(
      // isolate: 테두리 오버레이(z-2)를 셀 안에 가둔다. 없으면 셀이 stacking context 를
      // 만들지 않아 z-2 가 가격 칩(z-1)보다 위로 올라가 칩 위에 선이 그어진다.
      'relative isolate flex h-[106px] w-full flex-col items-center justify-center gap-2xs overflow-hidden rounded-[6px]', !hidePointUi && 'mb-[-14px]', isNoneSelected ? 'bg-strong-alternative' : 'bg-gray-50/50 group-hover:bg-neutral')}>
            <SlashCircleIcon size="xl" className="shrink-0 text-disabled" />
            <span className="text-body-sm-semibold text-alternative">
              {t('common:common.none')}
            </span>
            <span aria-hidden="true" className={cn('pointer-events-none absolute inset-0 z-[2] rounded-[6px] border', isNoneSelected ? 'border-2 border-[#6758ff8a]' : 'border-[#1c1b1f14]')} />
          </div>
          {/* 가격 칩이 있는 다른 타일들과 높이를 맞추는 자리. */}
          {!hidePointUi && <div className="relative z-[1] min-h-[26px]" />}
        </li>}
      {items.map(({
      id,
      shapeId,
      pathName,
      isNew,
      isHot,
      isOwned,
      isFree,
      price
    }) => {
      const isActive = shapeId === selectedShapeId;
      const isPaid = !isFree && typeof price === 'number' && price > 0;
      const justPurchased = shapeId === justPurchasedShapeId;
      return <li key={pathName} onClick={() => onSelect(id)} className="group flex cursor-pointer flex-col items-center">
              <div
        // 테두리는 아래 오버레이(span)가 그린다. 여기서 border 를 주면 CSS clip 경계가
        // padding box 라서 NEW 뱃지가 테두리 위로 올라올 수 없다(Figma 는 반대로
        // 테두리가 뱃지 위를 지나가고 뱃지는 모서리에 밀착한다).
        className={cn(
        // isolate: 위 '없음' 타일과 같은 이유. 테두리 오버레이(z-2)가 셀 밖으로
        // 새어나가 가격 칩(z-1) 위에 그어지는 것을 막는다.
        // overflow-hidden 은 유지하되, 구매 링은 이 안에서 inset 으로 퍼지므로
        // 잘리지 않는다(scale 1.18 이 셀 경계 안쪽에서 시작).
        'relative isolate h-[106px] w-full overflow-hidden rounded-[6px] transition-colors duration-150 ease-out motion-reduce:transition-none', !hidePointUi && 'mb-[-14px]',
        // 구매 직후 카드가 한 번 툭 튄다.
        justPurchased && 'animate-point-purchase-pop', isActive ? 'bg-strong-alternative' : 'bg-gray-50/50 group-hover:bg-neutral')}>
                <img src={pathName} alt="" loading="lazy" onError={event => {
            event.currentTarget.style.visibility = 'hidden';
          }} className="absolute left-1/2 top-[calc(50%+2px)] size-[80px] -translate-x-1/2 -translate-y-1/2 object-contain transition-[top] duration-150 ease-out group-hover:top-1/2 motion-reduce:transition-none" />
                {(isNew || isHot) && <span className={cn(
          // isolate + overflow-hidden: 아래 빛 스윕이 뱃지 모서리 안에서만 지나가게 가둔다.
          'absolute right-0 top-0 z-[1] flex items-center overflow-hidden rounded-bl-[6px] px-[4px] py-[2px] text-[11px] font-bold leading-[1.4] text-white shadow-sm isolate', isHot ? 'bg-gradient-to-r from-[#f268f5] to-[#ff737b]' : 'bg-gradient-to-r from-[#18d0b4] to-[#6ebfff]')}>
                    {isHot ? 'HOT' : 'NEW'}
                    {/*
                      카드 hover 시 흰 빛이 한 번 스친다(NEW·HOT 공통). group-hover 로 카드 전체를
                      hover 대상으로 삼고, 뱃지 안에서만 지나가도록 위 overflow-hidden 에 가둔다.
                     */}
                    <span aria-hidden="true" className="pointer-events-none absolute inset-0 -translate-x-full bg-[linear-gradient(105deg,transparent_30%,rgba(255,255,255,0.85)_50%,transparent_70%)] opacity-0 group-hover:animate-badge-shine motion-reduce:group-hover:animate-none" />
                  </span>}
                {/*
                  Figma(1342:30459): 카드 stroke 가 NEW/HOT 뱃지 위를 지나간다.
                  뱃지(z-1) 보다 위(z-2)에 테두리를 얹어 4면이 끊기지 않게 한다.
                  미선택 1px rgba(28,27,31,0.08) / 선택 2px Violet500@54%(#6758ff8a).
                 */}
                <span aria-hidden="true" className={cn('pointer-events-none absolute inset-0 z-[2] rounded-[6px] border', isActive ? 'border-2 border-[#6758ff8a]' : 'border-[#1c1b1f14]')} />
                {/* 구매 순간 보라 링이 확 퍼졌다 사라진다 = '샀다' 플래시. */}
                {justPurchased && <span aria-hidden="true" className="pointer-events-none absolute inset-0 z-[3] animate-point-purchase-ring rounded-[6px] border-2 border-[#6758ff] motion-reduce:hidden" />}
              </div>
              {!hidePointUi && <div className="relative z-[1] flex min-h-[26px] items-center justify-center">
                  {isPaid && !isOwned ? <Chip size="md" rounded="full" className={cn(chipClassName, 'gap-[2px]')} leftSlot={<JellyIcon size="xs" />}>
                      {price}
                    </Chip> : isOwned && !isFree ? <Chip size="md" rounded="full" className={cn(chipClassName, 'min-w-[32px] justify-center px-[8px]',
          // 방금 산 아이템은 체크 칩이 '툭' 나타나 획득을 알린다.
          justPurchased && 'animate-point-purchase-badge-in motion-reduce:animate-none')}>
                      <CheckBoldIcon size="2xs" className="text-assistive" />
                    </Chip> : <Chip size="md" rounded="full" className={cn(chipClassName, 'px-[10px]')}>
                      {t('common:pricing.free.free')}
                    </Chip>}
                </div>}
            </li>;
    })}
    </ul>;
};